import { get } from "lodash";
import {
  Modal,
  Flex,
  Button,
  Small,
  Icon,
  H3,
  Tabs,
  Box,
} from "orcs-design-system";
import PropTypes from "prop-types";
import React, { useCallback, useMemo, useState, useEffect } from "react";
import styled from "styled-components";

import icon from "src/config/icons";
import { GroupAttribute } from "src/custom-prop-types/groupAttribute";
import { reportError } from "src/services/errorReporting";
import { trackEvent } from "src/services/segment";
import { EVENT_TRACKING } from "src/consts/eventTracking";
import { useOrgBranding } from "src/hooks/orgConfig";
import UnsplashTab from "./tabs/UnsplashTab";
import CompanyImagesTab from "./tabs/CompanyImagesTab";

import { sizing, copywriting } from "./ImagePicker.config";
import {
  getSourceTypeKey,
  createOrAddImageAttribute,
} from "./ImagePicker.util";

const StyledModal = styled(Modal)`
  #modal-container {
    padding-left: 0;
    padding-right: 0;
  }
`;

const IMAGE_SOURCE_TYPES = {
  COMPANY_IMAGES: "company_images",
  STOCK_IMAGES: "stock_images",
};

const ImagePicker = ({
  attributes,
  isVisible,
  onUpdate,
  onHideModal,
  resourceTrackingType = EVENT_TRACKING.TEAM_UPDATED,
  attributeId = "bgImg",
}) => {
  const [activeTab, setActiveTab] = useState(() => {
    // Company Images is always available, so use it as default
    return IMAGE_SOURCE_TYPES.COMPANY_IMAGES;
  });
  const [selection, setSelection] = useState();
  const [isSaving, setIsSaving] = useState(false);
  const [onSaveError, setOnSaveError] = useState();

  const { branding } = useOrgBranding();

  // Compute available tabs to determine initial activeTab
  const availableTabs = useMemo(() => {
    return [
      {
        id: IMAGE_SOURCE_TYPES.COMPANY_IMAGES,
        label: "Company Images",
        isDisabled: false,
      },
      {
        id: IMAGE_SOURCE_TYPES.STOCK_IMAGES,
        label: "Stock Images",
        isDisabled: !!branding?.stockImagesDisabled,
      },
    ].filter((tab) => !tab.isDisabled);
  }, [branding]);

  const onConfirm = async () => {
    if (selection) {
      setOnSaveError(null);
      setIsSaving(true);

      try {
        const { imgUrl, altKey } = getSourceTypeKey(activeTab);
        const bgImgUrl = get(selection, imgUrl);
        const altDescription = get(selection, altKey);

        const newAttributes = createOrAddImageAttribute({
          attributes,
          bgImgUrl,
          altDescription,
          attributeId,
          subtype: activeTab,
        });
        await onUpdate(newAttributes);
        onHideModal();

        if (activeTab === IMAGE_SOURCE_TYPES.STOCK_IMAGES) {
          const unsplashDownloadEvent = `${get(
            selection,
            "links.download_location"
          )}/?client_id=${process.env.REACT_APP_UNSPLASH_ACCESS_KEY}`;

          // no need to wait for this to complete
          fetch(unsplashDownloadEvent);
        }
      } catch (mutationError) {
        reportError(mutationError);
        setOnSaveError(copywriting.FORM_SAVE_ERROR);
      }

      setIsSaving(false);

      trackEvent(resourceTrackingType, {
        unsplash_saved: activeTab === IMAGE_SOURCE_TYPES.STOCK_IMAGES,
        company_images_saved: activeTab === IMAGE_SOURCE_TYPES.COMPANY_IMAGES,
      });
    } else {
      setOnSaveError(copywriting.FORM_INVALID);
    }
  };

  const onCancel = useCallback(() => {
    setOnSaveError(null);
    onHideModal();
    trackEvent(resourceTrackingType, {
      cancelled: true,
    });
  }, [resourceTrackingType, onHideModal]);

  const onSelect = useCallback(
    (selected) => {
      setSelection(selected?.data);
      trackEvent(resourceTrackingType, {
        unsplash_selected: activeTab === IMAGE_SOURCE_TYPES.STOCK_IMAGES,
        company_images_selected:
          activeTab === IMAGE_SOURCE_TYPES.COMPANY_IMAGES,
      });
    },
    [resourceTrackingType, activeTab]
  );

  const tabs = useMemo(() => {
    return availableTabs.map((tab) => ({
      ...tab,
      onClick: () => {
        setActiveTab(tab.id);
        setSelection(null);
      },
      className: activeTab === tab.id ? "active" : "",
    }));
  }, [activeTab, availableTabs]);

  // Update activeTab if current tab becomes disabled or if it's null
  useEffect(() => {
    const isCurrentTabAvailable = availableTabs.some(
      (tab) => tab.id === activeTab
    );
    if ((!activeTab || !isCurrentTabAvailable) && availableTabs.length > 0) {
      setActiveTab(availableTabs[0].id);
    }
  }, [availableTabs, activeTab]);

  const modalHeader = <H3 px="r">{copywriting.TITLE}</H3>;

  const modalFooter = (
    <Flex flexWrap="wrap" width="100%" px="r">
      <Flex gap="s">
        <Button
          onClick={onConfirm}
          variant="success"
          iconLeft
          disabled={isSaving || !selection}
          isLoading={isSaving}
          data-testid="cp-unsplash-save-btn"
        >
          <Icon icon={["fas", "save"]} />
          Save
        </Button>
        <Button
          variant="ghost"
          onClick={onCancel}
          iconLeft
          data-testid="cp-unsplash-cancel-btn"
          tabIndex={0}
        >
          <Icon icon={["fas", "times"]} />
          Cancel
        </Button>
      </Flex>
      {onSaveError && (
        <Small variant="danger" fontWeight="bold" display="block" my="s">
          <Icon icon={icon.exclamation} mr="xs" />
          {onSaveError}
        </Small>
      )}
      {activeTab === IMAGE_SOURCE_TYPES.STOCK_IMAGES && (
        <Small color="greyDark" display="block" my="s" ml="auto">
          <em>{copywriting.ATTRIBUTION_LABEL}</em>
        </Small>
      )}
    </Flex>
  );

  return (
    <StyledModal
      visible={isVisible}
      onConfirm={onConfirm}
      onClose={onCancel}
      height={sizing.MODAL_HEIGHT}
      width={sizing.MODAL_WIDTH}
      maxHeight={sizing.MODAL_MAXHEIGHT}
      maxWidth={sizing.MODAL_MAXWIDTH}
      footerContent={modalFooter}
      headerContent={modalHeader}
    >
      {tabs.length > 1 && <Tabs tabsList={tabs} />}

      <Box p="r" height="100%" position="relative">
        {/* Always render content immediately to ensure tabbable elements are available for FocusTrap */}
        {activeTab === IMAGE_SOURCE_TYPES.STOCK_IMAGES && (
          <UnsplashTab onSelect={onSelect} />
        )}
        {activeTab === IMAGE_SOURCE_TYPES.COMPANY_IMAGES && (
          <CompanyImagesTab onSelect={onSelect} />
        )}
        {/* Ensure there's always a tabbable element for FocusTrap - render a hidden button */}
        <button
          type="button"
          style={{
            position: "absolute",
            left: "0",
            top: "0",
            width: "1px",
            height: "1px",
            opacity: 0.01,
            zIndex: -1,
          }}
          aria-hidden="true"
        />
      </Box>
    </StyledModal>
  );
};

ImagePicker.propTypes = {
  attributes: PropTypes.arrayOf(GroupAttribute),
  isVisible: PropTypes.bool,
  onUpdate: PropTypes.func,
  onHideModal: PropTypes.func,
  resourceTrackingType: PropTypes.string,
  attributeId: PropTypes.string,
};

export default React.memo(ImagePicker);
